import ReserveAvailabilityOvernight from './ReserveAvailabilityOvernight'
import ReserveAvailabilityDuration from './ReserveAvailabilityDuration'
import Http from '../../network/Http'
import {ApiResponse} from '../../network/response/ApiResponse'
import {
    FacilityReservation,
    NewDurationRentalFacilityReservation,
    NewOvernightRentalFacilityReservation,
    PaymentInformationForReservation,
    ReservationOption,
    RoomNumberView,
} from './ReserveFacilityModel'
import {ContentType} from '../../network/Constants'
import ReservationsOfWaitingConfirmation from './ReservationsOfWaitingConfirmation'
import {Nullable} from '../../app/TypeHelpers'
import {AVAILABLE_OR_NOT} from '../../CodeMaster'
import {Moment} from 'moment'
import {formatDateLocal} from '../../formatters/formatMoment'
import {convertJsonToCardInformation, ReservationStatusCardInformation} from './AdditionalChargeCardInformation'
import ReservationCardInformation from './ReservationCardInformation'
import {AdditionalChargeDate} from './ResidentReserveExtraReservationPage'

interface NewDurationRentalFacilityReservationToPost {
    confirmed: boolean

    startDate: string
    startTime: string
    endDate: string
    endTime: string
    reservationTitle: string
    sharedUseRoomNumberList: RoomNumberView[]
    options: ReservationOption[]

    paymentInformation: PaymentInformationForReservation
}

interface NewOvernightRentalFacilityReservationToPost {
    confirmed: boolean
    startDate: string
    stayCount: number
    reservationTitle: string
    sharedUseRoomNumberList: RoomNumberView[]
    options: ReservationOption[]

    paymentInformation: PaymentInformationForReservation
}

interface UpdateDurationRentalFacilityReservationToPut extends NewDurationRentalFacilityReservationToPost {
    reserveType: string
    reserveStatus: string
    version: number
}

interface UpdateOvernightRentalFacilityReservationToPut extends NewOvernightRentalFacilityReservationToPost {
    reserveType: string
    reserveStatus: string
    version: number
}

export interface ReservationOptionDetail extends ReservationOption {
    name: string
    paymentMethod: string
    unitName: string
}

export interface FacilityReservationDetail extends FacilityReservation {
    facilityReserveCategory: string
    options: ReservationOptionDetail[]
}

export default class ReservationsRepository {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    async getReserveStatusForOvernightRental(
        condominiumCd: string,
        facilityId: number,
        reservationId: Nullable<number>,
        reservationStartDate: string,
        daysCount: number,
    ): Promise<ReserveAvailabilityOvernight> {
        let urlString: string = `/api/condominiums/${condominiumCd}/facilities/${facilityId}/`
            + 'reservations-for-overnight?'
            + `startDate=${reservationStartDate}`
            + `&count=${daysCount}`
        if (reservationId !== null) {
            urlString += `&reserveId=${reservationId}`
        }

        const responseJson: ApiResponse<ReserveAvailabilityOvernight> =
            await this.http.get(urlString)
        const apiResponse: ApiResponse<ReserveAvailabilityOvernight> = ApiResponse.from(
            responseJson,
            ReserveAvailabilityOvernight.from,
        )
        return apiResponse.details
    }

    async getReserveStatusForDurationRental(
        condominiumCd: string,
        facilityId: number,
        reservationId: Nullable<number>,
        reservationStartDateString: string,
        reservationStartTimeString: string,
        reservationEndDateString: string,
        reservationEndTimeString: string,
        splitByDaily: boolean,
    ): Promise<ReserveAvailabilityDuration> {
        return this.getReserveStatusForDuration(
            condominiumCd,
            facilityId,
            reservationId,
            reservationStartDateString,
            reservationStartTimeString,
            reservationEndDateString,
            reservationEndTimeString,
            false,
            splitByDaily,
        )
    }

    async getReserveStatusForDurationDetail(
        condominiumCd: string,
        facilityId: number,
        reservationDateString: string,
    ): Promise<ReserveAvailabilityDuration> {
        return this.getReserveStatusForDuration(
            condominiumCd,
            facilityId,
            null,
            reservationDateString,
            '00:00',
            reservationDateString,
            '24:00',
            true,
            false,
        )
    }

    async getReserveStatusForDuration(
        condominiumCd: string,
        facilityId: number,
        reservationId: Nullable<number>,
        reservationStartDateString: string,
        reservationStartTimeString: string,
        reservationEndDateString: string,
        reservationEndTimeString: string,
        otherReserves: boolean,
        splitByDaily: boolean,
    ): Promise<ReserveAvailabilityDuration> {
        let urlString: string = `/api/condominiums/${condominiumCd}/facilities/${facilityId}`
            + '/reservations-for-duration?'
            + `startDateTime=${reservationStartDateString}T${reservationStartTimeString}`
            + `&endDateTime=${reservationEndDateString}T${reservationEndTimeString}`
            + `&otherReserves=${otherReserves}`
            + `&daily=${splitByDaily}`
        if (reservationId !== null) {
            urlString += `&reserveId=${reservationId}`
        }
        const responseJson: ApiResponse<ReserveAvailabilityDuration> =
            await this.http.get(urlString)
        const apiResponse: ApiResponse<ReserveAvailabilityDuration> = ApiResponse.from(
            responseJson,
            ReserveAvailabilityDuration.from,
        )
        return apiResponse.details
    }

    async prepareReserveFacilityForOvernight(
        condominiumCd: string,
        facilityId: number,
        reservation: NewOvernightRentalFacilityReservation): Promise<void> {

        const newReservationToPost: NewOvernightRentalFacilityReservationToPost = {
            ...reservation,
            confirmed: false,
        }

        await this.http.post(
            `/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations-for-overnight`,
            newReservationToPost,
            ContentType.JSON,
        )
    }

    async prepareReserveFacilityForDuration(
        condominiumCd: string,
        facilityId: number,
        reservation: NewDurationRentalFacilityReservation): Promise<void> {

        const newReservationToPost: NewDurationRentalFacilityReservationToPost = {
            ...reservation,
            confirmed: false,
        }

        await this.http.post(
            `/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations-for-duration`,
            newReservationToPost,
            ContentType.JSON,
        )
    }

    async reserveFacilityForDuration(
        condominiumCd: string,
        facilityId: number,
        reservation: NewDurationRentalFacilityReservation): Promise<void> {

        const newReservationToPost: NewDurationRentalFacilityReservationToPost = {
            ...reservation,
            confirmed: true,
        }

        await this.http.post(
            `/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations-for-duration`,
            newReservationToPost,
            ContentType.JSON,
        )
    }

    async reserveFacilityForOvernight(
        condominiumCd: string,
        facilityId: number,
        reservation: NewOvernightRentalFacilityReservation): Promise<void> {

        const newReservationToPost: NewOvernightRentalFacilityReservationToPost = {
            ...reservation,
            confirmed: true,
        }

        await this.http.post(
            `/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations-for-overnight`,
            newReservationToPost,
            ContentType.JSON,
        )
    }

    async getReservationsOfWaitingConfirmation(
        condominiumCd: string,
    ): Promise<ReservationsOfWaitingConfirmation> {
        const response: ApiResponse<ReservationsOfWaitingConfirmation> =
            await this.http.get(`/api/condominiums/${condominiumCd}/reservations?filter=waiting-confirmation`)
        const apiResponse: ApiResponse<ReservationsOfWaitingConfirmation> =
            ApiResponse.from(response, ReservationsOfWaitingConfirmation.from)
        return apiResponse.details
    }

    async getReservationsByMonth(
        condominiumCd: string,
        date: Moment,
    ): Promise<ReservationStatusCardInformation[]> {
        const dateString = formatDateLocal(date)
        const response: ApiResponse<ReservationStatusCardInformation[]> =
            await this.http.get(
                `/api/condominiums/${condominiumCd}/reservations?filter=by-month&date=${dateString}`
            )
        const apiResponse: ApiResponse<ReservationStatusCardInformation[]> =
            ApiResponse.fromArray(response, convertJsonToCardInformation)
        return apiResponse.details
    }

    async getFutureReservations(
        condominiumCd: string,
    ): Promise<ReservationCardInformation[]> {
        const response: ApiResponse<ReservationCardInformation[]> =
            await this.http.get(`/api/condominiums/${condominiumCd}/reservations?filter=future`)
        const apiResponse: ApiResponse<ReservationCardInformation[]> =
            ApiResponse.fromArray(response, ReservationCardInformation.from)
        return apiResponse.details
    }

    async getFacilityReservation(
        condominiumCd: string,
        facilityId: number,
        reserveId: number,
    ): Promise<FacilityReservationDetail> {
        return await this.http.get(`/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}`)
    }

    async prepareUpdateFacilityReservation(
        condominiumCd: string,
        facilityId: number,
        reserveId: number,
        updateReservation: FacilityReservation,
    ): Promise<void> {
        const {stayAvailability} = updateReservation
        const confirm: boolean = false
        if (AVAILABLE_OR_NOT.AVAILABLE.value === stayAvailability) {
            const updateReservationToPost = this.createUpdateReservationForOvernight(
                updateReservation,
                confirm,
            )
            await this.http.put(
                `/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations-for-overnight/${reserveId}`,
                updateReservationToPost,
            )
        } else {
            const updateReservationToPost = this.createUpdateReservationForDuration(
                updateReservation,
                confirm,
            )
            await this.http.put(
                `/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations-for-duration/${reserveId}`,
                updateReservationToPost,
            )
        }
    }

    async updateFacilityReservation(
        condominiumCd: string,
        facilityId: number,
        reserveId: number,
        updateReservation: FacilityReservation,
    ): Promise<void> {
        const {stayAvailability} = updateReservation
        const confirm: boolean = true
        if (AVAILABLE_OR_NOT.AVAILABLE.value === stayAvailability) {
            const updateReservationToPost = this.createUpdateReservationForOvernight(
                updateReservation,
                confirm,
            )
            await this.http.put(
                `/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations-for-overnight/${reserveId}`,
                updateReservationToPost,
            )
        } else {
            const updateReservationToPost = this.createUpdateReservationForDuration(
                updateReservation,
                confirm,
            )
            await this.http.put(
                `/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations-for-duration/${reserveId}`,
                updateReservationToPost,
            )
        }
        return
    }

    private createUpdateReservationForDuration(
        updateReservation: FacilityReservation,
        confirm: boolean,
    ): UpdateDurationRentalFacilityReservationToPut {
        return {
            startDate: updateReservation.startDate,
            startTime: updateReservation.startTime as string,
            endDate: updateReservation.endDate as string,
            endTime: updateReservation.endTime as string,
            reservationTitle: updateReservation.reservationTitle,
            sharedUseRoomNumberList: updateReservation.sharedUseRoomNumberList,
            options: updateReservation.options,
            paymentInformation: updateReservation.paymentInformation,
            confirmed: confirm,
            reserveType: updateReservation.reserveType,
            reserveStatus: updateReservation.reserveStatus,
            version: updateReservation.version,
        }
    }

    private createUpdateReservationForOvernight(
        updateReservation: FacilityReservation,
        confirm: boolean,
    ): UpdateOvernightRentalFacilityReservationToPut {
        return {
            startDate: updateReservation.startDate,
            stayCount: updateReservation.stayCount as number,
            reservationTitle: updateReservation.reservationTitle,
            sharedUseRoomNumberList: updateReservation.sharedUseRoomNumberList,
            options: updateReservation.options,
            paymentInformation: updateReservation.paymentInformation,
            confirmed: confirm,
            reserveType: updateReservation.reserveType,
            reserveStatus: updateReservation.reserveStatus,
            version: updateReservation.version,
        }
    }

    async cancelFacilityReservation(
        condominiumCd: string,
        facilityId: number,
        reserveId: number,
        reserveInfo: FacilityReservation,
    ): Promise<void> {
        await this.http.delete(`/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}?version=${reserveInfo.version}`)
    }

    async confirmFacilityReservation(
        condominiumCd: string,
        facilityId: number,
        reserveId: number,
        reserveInfo: FacilityReservation,
    ): Promise<void> {
        const {reserveStatus} = reserveInfo
        await this.http.patch(`/api/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}?version=${reserveInfo.version}`,
            {reserveStatus: reserveStatus},
        )
    }

    async getAdditionalCharge(
        condominiumCd: string,
        reserveId: number,
    ): Promise<AdditionalChargeDate> {
        return await this.http.get(`/api/condominiums/${condominiumCd}/extra-reservations/${reserveId}`)
    }

    async getRikbRedirectUrlFromReservations(
        condominiumCd: string,
        keyBoxId: string,
    ): Promise<ApiResponse<string>> {
        return await this.http.get(`/api/resident/condominiums/${condominiumCd}/rikb/redirect-url?keyBoxId=${keyBoxId}`)
    }
}
